import _ from 'lodash';

import { RESULT_GROUP_DIVIDER } from 'constants/documentExtractionFields';
import { DOC_FIELD_TYPES } from 'constants/documentFieldTypes';
import { checkIsContainerType } from 'utils/documentFieldTypes';

const getResultGroupId = ({ parentId, rowNumber }) =>
  _.isNil(parentId) ? `${rowNumber}` : `${parentId}${RESULT_GROUP_DIVIDER}${rowNumber}`;

const getGroupRowNumber = groupId => Number(_.last(`${groupId}`.split(RESULT_GROUP_DIVIDER)));

/**
 * Groups the nested nodes by their result group, ordered by row number. A composite group id is a
 * string, and string keys enumerate in insertion order, so without this the rows would render in
 * payload order instead of the ascending order plain numeric keys used to give.
 *
 * @param {Array<object>} nested
 * @returns {object}
 */
const groupNestedByResultGroup = nested =>
  _.fromPairs(
    _.sortBy(_.toPairs(_.groupBy(nested, field => field?.groupId)), ([groupId]) =>
      getGroupRowNumber(groupId),
    ),
  );

export const getExtractionFieldsTree = ({ extractionFields, extractionFieldsResults }) => {
  const extractionFieldsResultsGroupMap = _.groupBy(
    extractionFieldsResults,
    'documentExtractionFieldId',
  );

  const reduceFunc = fields =>
    fields.reduce(
      (acc, { documentFieldType, id, Children: children, parentId, name, orderNumber }) => {
        const isContainerType = checkIsContainerType(documentFieldType.typeDefinition.type);
        const extractionFieldsResults = extractionFieldsResultsGroupMap[id];

        if (
          (!isContainerType && !extractionFieldsResults?.length) ||
          (isContainerType && !children.length)
        ) {
          return acc;
        }

        const getNestedGroups = () => {
          if (!isContainerType) {
            return undefined;
          }

          const nested = reduceFunc(children);

          if (!nested.length) {
            return null;
          }

          return groupNestedByResultGroup(nested); // TODO: remove this when ViewExtractionField wil be reworked
        };

        const nestedGroups = getNestedGroups();

        if (isContainerType && nestedGroups === null) {
          return acc;
        }

        const fields = isContainerType
          ? [
              {
                id,
                nodeId: id,
                parentId,
                name,
                type: DOC_FIELD_TYPES.CONTAINER,
                nestedGroups,
                orderNumber,
              },
            ]
          : extractionFieldsResults.map(extractionFieldsResult => ({
              id,
              nodeId: extractionFieldsResult.id,
              documentFieldTypeId: documentFieldType.id,
              parentId,
              name,
              type: documentFieldType.typeDefinition.type,
              value: extractionFieldsResult.value,
              groupId: getResultGroupId(extractionFieldsResult),
              orderNumber,
              confidence: extractionFieldsResult.confidence ?? null,
              threshold: extractionFieldsResult.threshold ?? null,
            }));

        fields.forEach(field => {
          const insertIndex = acc.findIndex(field => field.orderNumber > orderNumber);

          if (insertIndex === -1) {
            acc[acc.length] = field;

            return acc;
          }

          acc.splice(insertIndex, 0, field);
        });

        return acc;
      },
      [],
    );

  return reduceFunc(extractionFields);
};
